import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageCircle, FiX, FiSend, FiArrowUpRight } from "react-icons/fi";

import { CONSULTATION_EMAIL } from "../config/contact";

const BOT_NAME = "PD Marketz";

const QUICK_REPLIES = [
  "SEO Services",
  "PPC Advertising",
  "Social Media",
  "Get Consultation",
];

const AUTO_REPLIES = {
  seo: "We offer complete SEO services including keyword research, on-page optimization, link building, and technical SEO to improve your rankings and drive organic traffic.",
  ppc: "Our PPC services cover Google Ads, campaign strategy, ad creation, audience targeting, and continuous optimization to maximize your ROI.",
  "social media": "We manage your social media presence across Instagram, Facebook, and LinkedIn — from content creation to audience engagement and growth.",
  consultation: "Great! You can book a free consultation by clicking the button below or reaching us on WhatsApp. We'll get back to you within minutes.",
  default: "Thanks for reaching out! Our team will be happy to help. You can also click 'Get Free Consultation' to connect with us directly.",
};

function getBotReply(text) {
  const lower = text.toLowerCase();
  if (lower.includes("seo") || lower.includes("search")) return AUTO_REPLIES.seo;
  if (lower.includes("ppc") || lower.includes("ads") || lower.includes("paid")) return AUTO_REPLIES.ppc;
  if (lower.includes("social") || lower.includes("instagram") || lower.includes("facebook")) return AUTO_REPLIES["social media"];
  if (lower.includes("consult") || lower.includes("free") || lower.includes("contact")) return AUTO_REPLIES.consultation;
  return AUTO_REPLIES.default;
}

function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: `Hi! 👋 I'm the ${BOT_NAME} assistant. How can I help you today?`,
    },
  ]);
  const [typing, setTyping] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const sendMessage = (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { role: "bot", text: getBotReply(trimmed) }]);
    }, 900);
  };

  const openEmail = () => {
    const subject = encodeURIComponent("Free Consultation Request — PD Marketz");
    const body = encodeURIComponent("Hi PD Marketz Team,\n\nI would like to request a free consultation.\n\nThank you.");
    window.location.href = `mailto:${CONSULTATION_EMAIL}?subject=${subject}&body=${body}`;
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="chatbot-panel"
            role="dialog"
            aria-label="Chat assistant"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-info">
                <div className="chatbot-avatar">PD</div>
                <div>
                  <strong>{BOT_NAME}</strong>
                  <span className="chatbot-status">
                    <span className="chatbot-dot" />
                    Online
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="chatbot-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <FiX />
              </button>
            </div>

            {/* Messages */}
            <div className="chatbot-messages" ref={listRef}>
              {messages.map((msg, i) => (
                <div key={i} className={`chatbot-bubble chatbot-bubble--${msg.role}`}>
                  {msg.text}
                </div>
              ))}
              {typing && (
                <div className="chatbot-bubble chatbot-bubble--bot chatbot-typing">
                  <span /><span /><span />
                </div>
              )}
            </div>

            {/* Quick replies */}
            <div className="chatbot-quick-replies">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="chatbot-quick-btn"
                  onClick={() => sendMessage(q)}
                >
                  {q}
                </button>
              ))}
            </div>

            {/* CTA */}
            <button type="button" className="chatbot-cta-btn" onClick={openEmail}>
              Get Free Consultation
              <FiArrowUpRight />
            </button>

            {/* Input */}
            <form
              className="chatbot-form"
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Type a message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoComplete="off"
              />
              <button type="submit" className="chatbot-send" aria-label="Send">
                <FiSend />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <motion.button
        type="button"
        className="chatbot-fab"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Close chat" : "Open chat"}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
              <FiX size={22} />
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
              <FiMessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
}

export default ChatbotWidget;
